import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {projectsApi} from '@/features/projects/services';

export interface ProjectRoleInfo {
  role: string | null;
  source: 'team' | 'creator' | 'global' | null;
  hasAccess: boolean;
  loading: boolean;
}

// Shared state across all hook instances to prevent duplicate fetches
const sharedFetchState = {
  fetching: new Map<string, AbortController>(),
  cache: new Map<string, { role: string | null; source: string | null; hasAccess: boolean; timestamp: number }>(),
};

export const clearProjectRoleCache = () => {
  sharedFetchState.fetching.forEach((controller) => {
    try {
      controller.abort();
    } catch {
      // no-op
    }
  });
  sharedFetchState.fetching.clear();
  sharedFetchState.cache.clear();
};

// Cache TTL: 5 minutes
const CACHE_TTL = 5 * 60 * 1000;

/**
 * Hook to get user's role in the current project
 * Checks team table for project-specific role
 * @returns ProjectRoleInfo with role, source, hasAccess, and loading state
 */
export const useProjectRole = (
  projectId?: string,
  project?: {
    created_by?: string;
    user_role?: string;
    user_role_source?: 'team' | 'creator' | 'global' | null;
  }
): ProjectRoleInfo => {
  const { id: routeProjectId } = useParams<{ id: string }>();
  const { user } = useAuth();
  
  const projectIdToUse = projectId || routeProjectId;
  
  // Track which project ID the current state belongs to
  const stateProjectIdRef = useRef<string | undefined>(projectIdToUse);
  
  // Compute role synchronously from project data (instant - no state needed)
  const computeRoleFromProject = (): ProjectRoleInfo | null => {
    // Use role from project data if available (fastest - no API call needed)
    if (project?.user_role && project?.user_role_source) {
      return {
        role: project.user_role,
        source: project.user_role_source,
        hasAccess: true,
        loading: false,
      };
    }
    
    // Optimistic rendering: if user created the project, assume project_manager role.
    // Both sides must be real, truthy values — `undefined === undefined` would
    // otherwise read as "yes, this user created it" for anyone whenever no
    // project object is passed in at all (e.g. useProjectRole(projectId) with no
    // second arg) and the auth user id hasn't resolved yet, incorrectly granting
    // optimistic PM access during that window.
    if (project?.created_by && user?.id && project.created_by === user.id) {
      return {
        role: 'project_manager',
        source: 'creator',
        hasAccess: true,
        loading: false,
      };
    }
    
    // Check shared cache
    const cached = sharedFetchState.cache.get(projectIdToUse || '');
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return {
        role: cached.role,
        source: cached.source as 'team' | 'creator' | 'global' | null,
        hasAccess: cached.hasAccess,
        loading: false,
      };
    }
    
    return null;
  };
  
  // Get role from project data immediately (synchronous)
  const roleFromProjectData = computeRoleFromProject();
  
  const [roleInfo, setRoleInfo] = useState<ProjectRoleInfo>(() => {
    // Use role from project data if available (instant), otherwise loading
    if (roleFromProjectData) {
      return roleFromProjectData;
    }
    return {
      role: null,
      source: null,
      hasAccess: false,
      loading: projectIdToUse !== undefined,
    };
  });

  // Update state immediately when project ID or project data changes
  useEffect(() => {
    const stateProjectId = stateProjectIdRef.current;
    const currentId = projectIdToUse;
    
    // Compute role from project data (synchronous)
    const computedRole = computeRoleFromProject();
    
    // Only reset if project ID actually changed (not just undefined -> undefined)
    if (stateProjectId !== currentId && (stateProjectId !== undefined || currentId !== undefined)) {
      // Project ID changed
      
      stateProjectIdRef.current = currentId;
      
      // Use computed role from project data (instant), or loading
      if (computedRole) {
        setRoleInfo(computedRole);
      } else {
        setRoleInfo({
          role: null,
          source: null,
          hasAccess: false,
          loading: currentId !== undefined,
        });
      }
    } else if (stateProjectId === currentId && computedRole && 
               (roleInfo.role !== computedRole.role || roleInfo.source !== computedRole.source)) {
      // Same project, but role from project data is available and different
      setRoleInfo(computedRole);
    }
  }, [projectIdToUse, project?.created_by, project?.user_role, project?.user_role_source, user?.id]);

  // Fetch role for current project only if not already available from project data or cache
  useEffect(() => {
    const currentId = projectIdToUse;
    
    // Don't fetch if no project ID or user
    if (!currentId || !user) {
      if (!currentId) {
        setRoleInfo({
          role: null,
          source: null,
          hasAccess: false,
          loading: false,
        });
      }
      return;
    }

    // Don't fetch if we already have role from project data (instant, no API call needed)
    if (project?.user_role && project?.user_role_source) {
      return;
    }

    // Check if already fetching for this project (shared across all hook instances)
    if (sharedFetchState.fetching.has(currentId)) {
      // Another hook instance is fetching - wait for it
      const checkCache = setInterval(() => {
        const cached = sharedFetchState.cache.get(currentId);
        if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
          clearInterval(checkCache);
          if (stateProjectIdRef.current === currentId) {
            setRoleInfo({
              role: cached.role,
              source: cached.source as 'team' | 'creator' | 'global' | null,
              hasAccess: cached.hasAccess,
              loading: false,
            });
          }
        } else if (!sharedFetchState.fetching.has(currentId)) {
          // Fetch completed but no cache - might have failed
          clearInterval(checkCache);
        }
      }, 100);
      
      // Cleanup interval after 5 seconds
      setTimeout(() => clearInterval(checkCache), 5000);
      return;
    }

    // Check cache first
    const cached = sharedFetchState.cache.get(currentId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      if (stateProjectIdRef.current === currentId) {
        setRoleInfo({
          role: cached.role,
          source: cached.source as 'team' | 'creator' | 'global' | null,
          hasAccess: cached.hasAccess,
          loading: false,
        });
      }
      return;
    }

    // Mark as fetching (shared across all instances)
    const abortController = new AbortController();
    sharedFetchState.fetching.set(currentId, abortController);

    const fetchRole = async () => {
      try {
        // Call backend endpoint to get project role
        const response = await projectsApi.getProjectRole(currentId);
        
        // Check if project ID changed during fetch or request was aborted
        if (stateProjectIdRef.current !== currentId || abortController.signal.aborted) {
          return;
        }
        
        if (response?.data) {
          const newRoleInfo: ProjectRoleInfo = {
            role: response.data.role,
            source: response.data.source as 'team' | 'creator' | 'global' | null,
            hasAccess: response.data.hasAccess ?? true,
            loading: false,
          };
          
          // Store in shared cache
          sharedFetchState.cache.set(currentId, {
            role: newRoleInfo.role,
            source: newRoleInfo.source,
            hasAccess: newRoleInfo.hasAccess,
            timestamp: Date.now(),
          });
          
          // Only update state if this is still the current project
          if (stateProjectIdRef.current === currentId && !abortController.signal.aborted) {
            setRoleInfo(newRoleInfo);
          }
        }
      } catch (error: any) {
        // Ignore abort errors
        if (error.name === 'AbortError' || abortController.signal.aborted) {
          return;
        }
        
        // Check if project ID changed during fetch
        if (stateProjectIdRef.current !== currentId) {
          return;
        }
        
        console.error('Error fetching project role:', error);
        // Don't set fallback role - just keep current state
      } finally {
        // Clear fetching flag (shared)
        sharedFetchState.fetching.delete(currentId);
      }
    };

    fetchRole();
  }, [projectIdToUse, user?.id, project?.user_role, project?.user_role_source]);

  // Always prefer role from project data if available (instant, synchronous)
  // This ensures no delay when switching projects
  const computedRole = computeRoleFromProject();
  if (computedRole) {
    return computedRole;
  }
  
  // Only return role info if it belongs to the current project ID
  // This prevents returning stale data from a different project
  if (stateProjectIdRef.current !== projectIdToUse) {
    // State belongs to a different project - return loading state for current project
    return {
      role: null,
      source: null,
      hasAccess: false,
      loading: projectIdToUse !== undefined,
    };
  }
  
  return roleInfo;
};

/**
 * Hook to check if user has a specific role in the current project
 * @param allowedRoles - Array of allowed roles
 * @param projectId - Optional project ID (uses route param if not provided)
 * @returns boolean indicating if user has one of the allowed roles
 */
export const useHasProjectRole = (
  allowedRoles: string[],
  projectId?: string
): boolean => {
  const roleInfo = useProjectRole(projectId);
  
  if (!roleInfo.role || roleInfo.loading) {
    return false;
  }

  return allowedRoles.includes(roleInfo.role);
};

/**
 * Hook to check if user has project access (any role or participant)
 * @param projectId - Optional project ID (uses route param if not provided)
 * @returns boolean indicating if user has access to the project
 */
export const useHasProjectAccess = (projectId?: string): boolean => {
  const roleInfo = useProjectRole(projectId);
  return roleInfo.hasAccess && !roleInfo.loading;
};

